/* Per-page cross-sell map — Website Domination SOP §6.2.
 *
 * Every page surfaces 4-6 contextual cards pointing at OTHER pages.
 * Doctrine: never annoying, always contextual — each card is a
 * logical next step from the page the buyer is on, not spam. The
 * CrossSellRail component renders these as the inline "While you're
 * here" strip (§6.1 component 3). */

import {
  BUNDLE_STARTER_USD,
  BUNDLE_PROFESSIONAL_USD,
  BUSINESS_TOTAL,
} from "./businesses";
import {
  INSIDER_PASS_USD,
  NOTION_COUNT,
  TEMPLATE_COUNT,
  THREE_D_MODEL_COUNT,
  ART_DESIGN_COUNT,
  fmt,
} from "./catalog-counts";

export type CrossSellCard = {
  href: string;
  kicker: string;
  title: string;
  blurb: string;
};

const CARD = {
  businesses: {
    href: "/browse-businesses",
    kicker: "Business-in-a-Box",
    title: `${BUSINESS_TOTAL} businesses you can start today`,
    blurb: `Notion CRM + Canva pack + AI Business Pack + Crush playbook, from $${BUNDLE_STARTER_USD}.`,
  },
  websites: {
    href: "/",
    kicker: "Websites",
    title: "Say it once. Watch your website build.",
    blurb: "Describe your site in one sentence and it builds live: game world, scroll story, 3D showcase, store or instant. One-time price, your code.",
  },
  notion: {
    href: "/notion",
    kicker: "Notion systems",
    title: `${fmt(NOTION_COUNT)} operating systems`,
    blurb: `The CRMs and dashboards that power the ${BUSINESS_TOTAL} prebuilt businesses.`,
  },
  templates: {
    href: "/templates",
    kicker: "Canva templates",
    title: `${fmt(TEMPLATE_COUNT)}+ marketing packs`,
    blurb: "Done-for-you marketing that pairs with every Notion CRM.",
  },
  threeD: {
    href: "/3d-models",
    kicker: "3D models",
    title: `${THREE_D_MODEL_COUNT} import-ready 3D models`,
    blurb: "GLB / STL across 9 themed packs.",
  },
  wallArt: {
    href: "/digital-art",
    kicker: "Wall art",
    title: `${fmt(ART_DESIGN_COUNT)} wall art designs`,
    blurb: "Every aesthetic, every niche — printable or print-and-ship.",
  },
  starterKits: {
    href: "/starter-kits",
    kicker: "Starter kits",
    title: "Niche starter kits at $199",
    blurb: "Lighter than a full bundle — templates + launch guide for one niche.",
  },
  niches: {
    href: "/industries",
    kicker: "Niche pages",
    title: "2,411 niche-specific kits",
    blurb: "Mapped down to the actual workflow of your exact niche.",
  },
  proTier: {
    href: "/browse-businesses",
    kicker: "Most picked",
    title: `Professional tier — $${BUNDLE_PROFESSIONAL_USD}`,
    blurb: "AI Business Pack, Spiker SOPs, and the Crush playbook included.",
  },
} satisfies Record<string, CrossSellCard>;

/* Which cards show where (SOP §6.2 table). Key = page identity the
 * caller passes; order matters (first card gets the lead slot). */
export const CROSS_SELL_MAP: Record<string, CrossSellCard[]> = {
  home: [CARD.businesses, CARD.websites, CARD.notion, CARD.templates, CARD.wallArt],
  "browse-businesses": [CARD.websites, CARD.notion, CARD.templates, CARD.starterKits],
  business: [CARD.websites, CARD.notion, CARD.templates, CARD.niches],
  notion: [CARD.businesses, CARD.templates, CARD.websites, CARD.wallArt],
  templates: [CARD.businesses, CARD.notion, CARD.websites, CARD.wallArt],
  "3d-models": [CARD.businesses, CARD.websites, CARD.wallArt, CARD.notion],
  "digital-art": [CARD.businesses, CARD.websites, CARD.notion, CARD.templates],
  membership: [CARD.businesses, CARD.notion, CARD.templates, CARD.wallArt],
  "starter-kits": [CARD.businesses, CARD.websites, CARD.notion, CARD.templates],
};

export function getCrossSells(pageKey: string): CrossSellCard[] {
  return CROSS_SELL_MAP[pageKey] ?? [CARD.businesses, CARD.websites];
}
